import React, { useState, useEffect } from 'react';
import { withStyles } from '@material-ui/core/styles';
import { Autocomplete } from '../Autocomplete';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  LinearProgress,
  Typography,
} from '@material-ui/core';
import PropTypes from 'prop-types';

// SERVICES
import { getValidOrdersForUser } from '../../services';

const styles = props => ({
  darkBlueButton: {
    backgroundColor: '#315382',
    '& *': {
      color: 'white',
    },
  },
});

export const BulkAssignDialog = withStyles(styles)(props => {
  const {
    // classes,
    person, // { id: null, name: '' } if this is available, use this instead of selecting from person options
    personOptions,
    personValueField,
    personLabel,
    personPlaceholder,
    rowData,
    show,
    dialogValues,
    setDialogValues,
    onConfirm,
    allowAutoReassignment,
    allowBlankAssignment,
    blankAssignmentWarningMessage,
    onClose,
  } = props;

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState(null);
  const [userRestrictedOrderLineIdMessage, setUserRestrictedOrderLineIdMessage] = useState(null);
  const [validatingOrders, setValidatingOrders] = useState(false);
  const [autoReassignData, setAutoReassignData] = useState(null);
  const [personResultData, setPersonResultData] = useState(null);
  const [showBlankAssignmentConfirm, setShowBlankAssignmentConfirm] = useState(false);

  useEffect(() => {
    if (!person) {
      const aspNetUserId = dialogValues[personValueField] ? dialogValues[personValueField].aspNetUserId : null;
      if (aspNetUserId) {
        getValidOrdersForUserAsync(aspNetUserId);
      }
    }
  }, [dialogValues]);

  useEffect(() => {
    // clear the selected user and any errors on grid close.  This will force a re-selection on next open
    if (!show) {
      setAutoReassignData(null);
      setPersonResultData(null);
      setUserRestrictedOrderLineIdMessage(null);
      setShowBlankAssignmentConfirm(false);
      !person &&
        setDialogValues({
          ...dialogValues,
          [personValueField]: null,
        });
    }
  }, [show]);

  const getValidOrdersForUserAsync = async aspNetUserId => {
    setValidatingOrders(true);
    const validOrderLineIds = await getValidOrdersForUser(aspNetUserId);
    const selectedOrderLineIds = rowData;
    const invalidOrderLineIds = selectedOrderLineIds.filter(
      selectedOrderLineId => !validOrderLineIds.includes(selectedOrderLineId),
    );
    if (invalidOrderLineIds.length > 0) {
      setUserRestrictedOrderLineIdMessage(
        `Warning: The selected user is typically restricted from assignment on the following orders: ${invalidOrderLineIds.join(
          ', ',
        )}. Are you sure you want to continue?`,
      );
    } else {
      setUserRestrictedOrderLineIdMessage(null);
    }
    setValidatingOrders(false);
  };

  const handleAssignClick = () => {
    const personId = !!person
      ? person.id
      : dialogValues[personValueField]
      ? dialogValues[personValueField].value
      : null;

    if (!personId && allowBlankAssignment && !autoReassignData && !personResultData) {
      return setShowBlankAssignmentConfirm(true);
    }

    return assignOrders();
  };

  const assignOrders = async () => {
    // short circuit and reload the main grid
    if (!!autoReassignData && person) {
      return onClose(true);
    }
    if (!!autoReassignData && !person) {
      return onClose(true);
    }
    if (!!personResultData) {
      return onClose(true);
    }

    setSubmitMessage(null);
    setAutoReassignData(null);
    const personId = !!person
      ? person.id
      : dialogValues[personValueField]
      ? dialogValues[personValueField].value
      : null;
    const orderTickets = rowData;

    if (!orderTickets.length) {
      setSubmitMessage('You must select at least one order ticket to assign');
      return setTimeout(() => {
        setSubmitMessage(null);
      }, 3000);
    }

    if (!personId && !allowAutoReassignment && !allowBlankAssignment) {
      setSubmitMessage('Please choose an option for assignment');
      return setTimeout(() => {
        setSubmitMessage(null);
      }, 3000);
    }

    try {
      setIsSubmitting(true);
      const data = await onConfirm(orderTickets, personId);

      if (allowAutoReassignment && !personId && data) {
        setAutoReassignData(data);
      }
      if (allowAutoReassignment && personId && data) {
        setAutoReassignData(null);
        setPersonResultData(data?.results);
      }

      setSubmitMessage('Orders assigned successfully!');
      setTimeout(() => {
        setSubmitMessage(null);

        console.log(personResultData, 'personResultData')
        console.log(allowAutoReassignment, 'allowAutoReassignment')
        console.log(personId, 'personId')
        if (!allowAutoReassignment ) {
          console.log("here")
          onClose(true);
        }
      }, 1000);
    } catch (error) {
      setSubmitMessage(error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={show} onClose={() => onClose(false)} aria-labelledby="form-dialog-title">
      <DialogTitle>Bulk Assign</DialogTitle>
      <DialogContent
        dividers
        style={{
          minWidth: 500,
          minHeight: 400,
          borderTop: '0.8px solid #ddd',
          borderBottom: '0.8px solid #ddd',
          paddingTop: 20,
          pointerEvents: isSubmitting ? 'none' : 'auto',
          opacity: isSubmitting ? 0.5 : 1,
        }}
      >
        <div>Selected Order Tickets:</div>
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            marginTop: 3,
            marginBottom: 15,
            minHeight: 25,
            border: !rowData || !rowData.length ? '1px solid #999' : '1px solid black',
            borderRadius: 3,
            padding: 5,
          }}
        >
          {rowData && !!rowData.length ? rowData.join(', ') : ''}
          {!rowData || (!rowData.length && <div style={{ color: '#999' }}>No orders were selected</div>)}
        </div>

        {person ? (
          <Typography>
            {!!personLabel ? `${personLabel}: ` : 'Assignee: '}
            {person.name}
          </Typography>
        ) : (
          <Autocomplete
            options={personOptions}
            disabled={!personOptions}
            value={dialogValues[personValueField]}
            onChange={opt => {
              const tempDialogValues = Object.assign({}, dialogValues);
              tempDialogValues[personValueField] = opt;
              setDialogValues(tempDialogValues);
            }}
            label={personLabel}
            placeholder={personPlaceholder}
          />
        )}
        {userRestrictedOrderLineIdMessage && (
          <p
            style={{
              color: 'red',
              fontWeight: 'bold',
              textAlign: 'center',
            }}
          >
            {userRestrictedOrderLineIdMessage}
          </p>
        )}

        {allowAutoReassignment && !person && !dialogValues[personValueField] && !autoReassignData && (
          <p
            style={{
              color: 'red',
              fontWeight: 'bold',
              textAlign: 'center',
            }}
          >
            Leaving the inspector field blank will allow you to run auto reassignment on the selected orders.
          </p>
        )}
        {!!autoReassignData && (
          <div className="col-start-center">
            <h4>Assignment Results</h4>
            {autoReassignData.map(({ orderLineId, inspector }) => (
              <div className="row-center-center full-width mt-5">
                <div className="flex-1 text-right mr-15">{orderLineId}</div>
                <div
                  className="flex-1 text-left ml-15"
                  style={{
                    color: !!inspector ? 'inherit' : 'red',
                  }}
                >
                  {!!inspector ? inspector : 'none'}
                </div>
              </div>
            ))}
          </div>
        )}{!!personResultData && (
          <div className="col-start-center">
            <h4>Assignment Results</h4>
            {personResultData.map(({ orderLineId, inspector }) => (
              <div className="row-center-center full-width mt-5">
                <div className="flex-1 text-right mr-15">{orderLineId}</div>
                <div
                  className="flex-1 text-left ml-15"
                  style={{
                    color: !!inspector ? 'inherit' : 'red',
                  }}
                >
                  {!!inspector ? inspector : 'none'}
                </div>
              </div>
            ))}
          </div>
        )}
      </DialogContent>
      <DialogActions
        style={{
          flexDirection: 'row',
          justifyContent: 'space-between',
        }}
      >
        <Button
          onClick={() => onClose(false)}
          variant="contained"
          style={{ backgroundColor: '#315382', color: 'white' }}
        >
          Cancel
        </Button>
        <div style={{ flex: 1, paddingLeft: 10, paddingRight: 10 }}>
          {isSubmitting && <LinearProgress />}
          {submitMessage && <div className="text-center">{submitMessage}</div>}
          {validatingOrders && (
            <div className="mb-20">
              <div className="text-center">Validating...</div>
              <LinearProgress />
            </div>
          )}
        </div>
        <Button
          disabled={
            isSubmitting ||
            validatingOrders ||
            (!allowAutoReassignment &&
              !allowBlankAssignment &&
              !person &&
              !dialogValues[personValueField])
          }
          onClick={handleAssignClick}
          variant="contained"
          style={{ backgroundColor: '#315382', color: 'white' }}
        >
          {!!userRestrictedOrderLineIdMessage && `Yes, `}
          {allowAutoReassignment && !person && !dialogValues[personValueField]
            ? autoReassignData
              ? 'Done'
              : 'Auto Assign Orders'
            : personResultData
              ? 'Done'
              : allowBlankAssignment && !person && !dialogValues[personValueField]
                ? 'Unassign Orders'
                : 'Assign Orders'}
        </Button>
      </DialogActions>

      <Dialog
        open={showBlankAssignmentConfirm}
        onClose={() => setShowBlankAssignmentConfirm(false)}
        aria-labelledby="blank-assignment-confirm-title"
      >
        <DialogTitle id="blank-assignment-confirm-title">Confirm Unassignment</DialogTitle>
        <DialogContent dividers>
          <Typography>{blankAssignmentWarningMessage}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowBlankAssignmentConfirm(false)} variant="contained">
            Cancel
          </Button>
          <Button
            onClick={() => {
              setShowBlankAssignmentConfirm(false);
              assignOrders();
            }}
            variant="contained"
            style={{ backgroundColor: '#315382', color: 'white' }}
          >
            Yes, Unassign Orders
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
});

BulkAssignDialog.propTypes = {
  // inspectorOptions:
  rowData: PropTypes.array.isRequired,
  show: PropTypes.bool.isRequired,
  setDialog: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  allowAutoReassignment: PropTypes.bool,
  allowBlankAssignment: PropTypes.bool,
  blankAssignmentWarningMessage: PropTypes.string,

  // dialogValues
  setDialogValues: PropTypes.func,
};

BulkAssignDialog.defaultProps = {
  allowAutoReassignment: false,
  allowBlankAssignment: false,
  blankAssignmentWarningMessage:
    'Submitting without a selected user will unassign the selected orders and return them to the queue. Are you sure you want to continue?',
};
